import { Fragment, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ERROR } from 'src/styles/colors';

import { getWorkspacePanelTabsForWorkspaceAndCurrentUser } from 'src/selectors/workspaces';

import useTabsWithRoutes from '../../../../../hooks/useTabsWithRoutes';
import useWorkspaceInformation from './useWorkspaceInformation';

import WorkspaceGeneralSettings from './WorkspaceGeneralSettings/WorkspaceGeneralSettings';
import BillingSettingsContainer from '../../../../../containers/BillingSettingsContainer/BillingSettingsContainer';
import PlanSettingsContainer from '../../../../../containers/PlanSettingsContainer/PlanSettingsContainer';
import WorkspaceProjectsTab from '../../../../../components/WorkspaceProjectsTab/WorkspaceProjectsTab';
import WorkspaceMemberListContainer from '../../../../../containers/WorkspaceMemberListContainer/WorkspaceMemberListContainer';
import WorkspaceProvisioningTab from '../WorkspaceProvisioningTab/WorkspaceProvisioningTab';

export type Tab = {
	name: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	component: React.ComponentType<any>;
	onlyOwner: boolean;
	key: string;
};

export const WORKSPACE_PANEL_TABS: Tab[] = [
	{
		name: 'Plan', component: PlanSettingsContainer, onlyOwner: true, key: 'plan'
	},
	{
		name: 'Billing', component: BillingSettingsContainer, onlyOwner: true, key: 'billing'
	},
	{
		name: 'Members', component: WorkspaceMemberListContainer, onlyOwner: false, key: 'members'
	},
	{
		name: 'Projects', component: WorkspaceProjectsTab, onlyOwner: true, key: 'projects'
	},
	{
		name: 'General', component: WorkspaceGeneralSettings, onlyOwner: false, key: 'general'
	},
	{
		name: 'Provisioning', component: WorkspaceProvisioningTab, onlyOwner: true, key: 'provisioning'
	}
];

type UseTabsWithRoutesResult = [ Tab[], number, ( index: number ) => void ];

export const useWorkspaceSettingsPanel = ( workspaceID: number ) => {
	const workspace = useWorkspaceInformation( workspaceID );
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const tabs: Tab[] = useSelector( ( state: any ) => getWorkspacePanelTabsForWorkspaceAndCurrentUser(
		// @ts-expect-error - TODO: fix this
		state, { workspaceID, tabs: WORKSPACE_PANEL_TABS }
	) );

	const [ availableTabs, selectedTabIndex, onTabChanged ] = useTabsWithRoutes(
		tabs, 'section', `/user/workspaces/${workspace.id}`
	) as UseTabsWithRoutesResult;

	const tabItems = availableTabs.map( ( { name, key } ) => {
		if ( key === 'plan' && workspace?.showWarningIcon ) {
			return {
				label: name,
				icon: 'error',
				iconColor: ERROR
			};
		}
		return { label: name };
	} );

	const CurrentTabComponent = useMemo( () => (
		availableTabs[ selectedTabIndex ] && availableTabs[ selectedTabIndex ].component
	) || Fragment, [ availableTabs, selectedTabIndex ] );

	return {
		workspace,
		tabItems,
		selectedTabIndex,
		onTabChanged,
		CurrentTabComponent
	};
};
